import {
  MarketList,
  MarketRow,
  MarketInputSearch,
  MarketAccessory,
  MarketActivityIndicator,
  MarketPill,
  MarketHeader,
} from '@market/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketChevronRightIcon, MarketLocationPinIcon } from '@squareup/market-react/icons';
import type { BrandLocationConfiguration, GetMerchantDefaultBrandResponse } from '@squareup/shared-types-protos/squareup/cash/local/v1/seller/cash_app_local_seller_service';
import { EnablementStatus } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/enums.js';
import type { FulfillmentConfiguration, DeliverySettings, PickupSettings } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/models.js';
import type { Location } from '@squareup/shared-types-protos/squareup/connect/v2/resources/location';
import { useCashAppDefaultBrand } from '../../hooks/useCashApp.js';
import { FulfillmentType } from '../../hooks/useFulfillmentConfiguration.js';
import { getPrepTimeNumberForSingleConfig } from '../../util/prep-time-utils.js';
import styles from './prep-time-location-modal-content.module.css';

export interface PrepTimeLocationModalContentProps {
  locations: Location[];
  locationIdToFulfillmentConfigMap: Record<string, FulfillmentConfiguration | undefined>;
  onClose: () => void;
  onLocationSelect: (locationId: string) => void;
}

export function PrepTimeLocationModalContent({
  locations,
  locationIdToFulfillmentConfigMap,
  onClose,
  onLocationSelect,
}: PrepTimeLocationModalContentProps) {
  const { t } = useTranslation();
  const { data: defaultBrandData } = useCashAppDefaultBrand();

  // Calculate enabled locations by fulfillment type
  const enabledLocationsIdsByFulfillment = useMemo(() => {
    const result: Record<FulfillmentType, string[]> = {
      [FulfillmentType.Pickup]: [],
      [FulfillmentType.Delivery]: [],
    };

    Object.entries(locationIdToFulfillmentConfigMap).forEach(([locationId, config]) => {
      if (config?.pickupSettings?.enablementStatus === EnablementStatus.ENABLED) {
        result[FulfillmentType.Pickup].push(locationId);
      }
      if (config?.deliverySettings?.enablementStatus === EnablementStatus.ENABLED) {
        result[FulfillmentType.Delivery].push(locationId);
      }
    });

    return result;
  }, [locationIdToFulfillmentConfigMap]);

  const isPendingData = locations.length === 0 || Object.keys(locationIdToFulfillmentConfigMap).length === 0;

  // Helper function to get ordering status for a location
  const getOrderingStatusForLocation = (locationId: string, brandData: GetMerchantDefaultBrandResponse): { isOrderingPaused: boolean; statusText?: string } => {
    // Check location-specific configuration only
    const locationConfig = brandData?.locationConfigurations?.find(
      (config: BrandLocationConfiguration) => config.unitToken === locationId,
    );

    if (locationConfig?.orderConfiguration?.orderingPaused) {
      return { isOrderingPaused: true, statusText: t('prepTime.orderingOff') };
    }

    return { isOrderingPaused: false };
  };

  // Helper function to format prep time duration to readable text with "prep time" suffix
  const formatPrepTime = (fulfillmentConfigurationByType?: PickupSettings | DeliverySettings): string => {
    const prepTimeValue = getPrepTimeNumberForSingleConfig(fulfillmentConfigurationByType);
    if (!prepTimeValue) {
      return t('prepTime.noPrep');
    }
    return t('prepTime.minutesPrepTime', { count: prepTimeValue });
  };

  const getFulfillmentSubtext = (
    locationId: string,
    configs: Record<string, FulfillmentConfiguration | undefined>,
    enabledIds: Record<FulfillmentType, string[]>,
  ): string => {
    const isPickupEnabled = enabledIds[FulfillmentType.Pickup]?.includes(locationId);
    const isDeliveryEnabled = enabledIds[FulfillmentType.Delivery]?.includes(locationId);

    const locationConfig: FulfillmentConfiguration | undefined = configs?.[locationId];
    if (!locationConfig) {
      return t('prepTime.noFulfillmentConfigured');
    }

    const pickupPrepTime = locationConfig.pickupSettings?.prepTimeDuration as string;
    const deliveryPrepTime = locationConfig.deliverySettings?.prepTimeDuration as string;

    // Build fulfillment type labels
    const fulfillmentTypes: string[] = [];
    if (isPickupEnabled) {
      fulfillmentTypes.push(t('prepTime.pickup'));
    }
    if (isDeliveryEnabled) {
      fulfillmentTypes.push(t('prepTime.delivery'));
    }

    if (fulfillmentTypes.length === 0) {
      return t('prepTime.noFulfillmentEnabled');
    }

    // Check prep times
    if (isPickupEnabled && isDeliveryEnabled) {
      // Both enabled - check if prep times are the same
      if (pickupPrepTime === deliveryPrepTime) {
        return `${fulfillmentTypes.join(' · ')} · ${formatPrepTime(locationConfig.pickupSettings)}`;
      }
      return `${fulfillmentTypes.join(' · ')} · ${t('prepTime.prepTimesVary')}`;
    }
    if (isPickupEnabled) {
      // Only pickup enabled
      return `${t('prepTime.pickup')} · ${formatPrepTime(locationConfig.pickupSettings)}`;
    }
    if (isDeliveryEnabled) {
      // Only delivery enabled
      return `${t('prepTime.delivery')} · ${formatPrepTime(locationConfig.deliverySettings)}`;
    }

    return t('prepTime.noFulfillmentEnabled');
  };

  return (
    <>
      <MarketHeader showNavigation onMarketHeaderNavigate={onClose}>
        <h2>{t('prepTime.updatePrepTime')}</h2>
        <small slot="subheading">
          {t('prepTime.chooseLocationToUpdate')}
        </small>
      </MarketHeader>

      <main>
        <MarketList className={styles.locationRows}>
          <MarketInputSearch
            slot="search"
            placeholder={t('common.search')}
          />

          {!isPendingData
            ? (
              <>
                {locations
                  .sort((a, b) => {
                    const nameA = (a.name || '').toLowerCase();
                    const nameB = (b.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((location) => {
                    const fulfillmentSubtext = getFulfillmentSubtext(
                      location.id || '',
                      locationIdToFulfillmentConfigMap,
                      enabledLocationsIdsByFulfillment,
                    );

                    const { isOrderingPaused, statusText } = defaultBrandData
                      ? getOrderingStatusForLocation(location.id || '', defaultBrandData)
                      : { isOrderingPaused: false, statusText: '' };

                    return (
                      <MarketRow
                        key={location.id}
                        data-testid={`location-row-${location.id}`}
                        value={location.name}
                        onClick={() => onLocationSelect(location.id || '')}
                      >
                        <MarketAccessory size="icon" slot="leading-accessory">
                          <MarketLocationPinIcon />
                        </MarketAccessory>

                        <div slot="label" className={styles.labelContainer}>
                          <div className={styles.businessName}>
                            {location.name || t('location.unnamed')}
                          </div>
                          {isOrderingPaused && (
                            <MarketPill className={styles.orderingStatusPill} size="small" variant="warning">
                              {statusText}
                            </MarketPill>
                          )}
                        </div>

                        <p slot="subtext">
                          {fulfillmentSubtext}
                        </p>

                        <MarketAccessory slot="trailing-accessory" className={styles.drillIcon}>
                          <MarketChevronRightIcon />
                        </MarketAccessory>
                      </MarketRow>
                    );
                  })}
              </>
            )
            : (
              <MarketActivityIndicator data-testid="loading-indicator" />
            )}
        </MarketList>
      </main>
    </>
  );
}
